/**
 * Global banner shown across the app whenever a super admin has impersonated
 * another user via the Super Admin dashboard. Restoring exits impersonation by
 * swapping the backed-up admin token back into `edsupp_token` and reloading.
 */
import React, { useEffect, useState } from 'react';

const ADMIN_BACKUP_KEY = 'edsupp_admin_token_backup';
const IMPERSONATED_INFO_KEY = 'edsupp_impersonated_user';

const ImpersonationBanner = () => {
  const [info, setInfo] = useState(() => {
    if (typeof window === 'undefined') return null;
    if (!localStorage.getItem(ADMIN_BACKUP_KEY)) return null;
    try {
      return JSON.parse(localStorage.getItem(IMPERSONATED_INFO_KEY) || 'null');
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sync = () => {
      if (!localStorage.getItem(ADMIN_BACKUP_KEY)) {
        setInfo(null);
        return;
      }
      try {
        setInfo(JSON.parse(localStorage.getItem(IMPERSONATED_INFO_KEY) || 'null'));
      } catch {
        setInfo(null);
      }
    };
    window.addEventListener('storage', sync);
    window.addEventListener('impersonation:changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('impersonation:changed', sync);
    };
  }, []);

  if (!info) return null;

  const stopImpersonating = () => {
    const backup = localStorage.getItem(ADMIN_BACKUP_KEY);
    if (backup) {
      localStorage.setItem('edsupp_token', backup);
    } else {
      localStorage.removeItem('edsupp_token');
    }
    localStorage.removeItem(ADMIN_BACKUP_KEY);
    localStorage.removeItem(IMPERSONATED_INFO_KEY);
    window.dispatchEvent(new Event('impersonation:changed'));
    // Force a real reload — if the user is already on /dashboard, setting
    // location.href to the same path won't trigger navigation in browsers.
    if (window.location.pathname === '/dashboard') {
      window.location.reload();
    } else {
      window.location.assign('/dashboard');
    }
  };

  const label = info.name || info.email || 'user';
  const role = info.role ? ` (${info.role})` : '';

  return (
    <div className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <span className="text-sm font-medium">
          👁️ Impersonating <strong>{label}</strong>{role} — you are signed in as another user.
        </span>
        <button
          type="button"
          onClick={stopImpersonating}
          className="px-3 py-1 rounded bg-amber-700 hover:bg-amber-800 text-sm font-semibold whitespace-nowrap"
        >
          Stop Impersonating
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
export { ADMIN_BACKUP_KEY, IMPERSONATED_INFO_KEY };
